import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { fmtMoney, formatApiErrorDetail } from "../lib/api";
import { useSettings } from "../context/SettingsContext";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";
import { Vault, ChartPieSlice, Buildings, Users, Trash, ShieldCheck, Gear, CheckCircle, Clock, DownloadSimple, Prohibit, ArrowCounterClockwise } from "@phosphor-icons/react";

const TABS = [
  ["overview", "Vue d'ensemble", <ChartPieSlice size={18} />],
  ["cautions", "Cautions & Réservations", <Vault size={18} />],
  ["properties", "Biens", <Buildings size={18} />],
  ["users", "Utilisateurs", <Users size={18} />],
  ["settings", "Paramètres du site", <Gear size={18} />],
];

const COLORS = ["#9a3412", "#0f172a", "#d97706", "#0284c7"];
const TYPE_LABEL = { caution: "Caution", event: "Courte durée", longterm: "Location" };

export default function AdminPanel() {
  const { refresh: refreshSettings } = useSettings();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [props, setProps] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(null);

  const loadAll = async () => {
    try {
      const [s, b, p, u, cfg] = await Promise.all([
        api.get("/admin/stats"), api.get("/admin/bookings"), api.get("/admin/properties"),
        api.get("/admin/users"), api.get("/settings"),
      ]);
      setStats(s.data); setBookings(b.data); setProps(p.data); setUsers(u.data);
      setForm({ site_name: cfg.data.site_name, hero_title: cfg.data.hero_title,
        hero_subtitle: cfg.data.hero_subtitle, commission_rate: cfg.data.commission_rate });
    } catch (e) {
      toast.error("Impossible de charger les données admin.");
    }
  };
  useEffect(() => { loadAll(); }, []);

  const delProp = async (id) => {
    await api.delete(`/admin/properties/${id}`);
    toast.success("Bien supprimé");
    loadAll();
  };

  const setUserRole = async (uid, role) => {
    try {
      await api.put(`/admin/users/${uid}/role`, { role });
      toast.success("Rôle mis à jour");
      loadAll();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const toggleBlock = async (u) => {
    try {
      await api.put(`/admin/users/${u.user_id}/block`, { blocked: !u.blocked });
      toast.success(u.blocked ? "Compte débloqué" : "Compte bloqué");
      loadAll();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/export/commissions.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "commissions.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Échec de l'export"); }
  };

  const certifyProp = async (id, certified) => {
    try {
      await api.put(`/admin/properties/${id}/certify`, { certified });
      toast.success(certified ? "Bien certifié" : "Certification retirée");
      loadAll();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const releasePayout = async (id) => {
    try {
      await api.put(`/admin/bookings/${id}/payout`);
      toast.success("Virement au propriétaire marqué effectué");
      loadAll();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const saveSettings = async () => {
    try {
      await api.put("/admin/settings", { ...form, commission_rate: Number(form.commission_rate) });
      toast.success("Paramètres enregistrés");
      refreshSettings();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const pieData = stats ? Object.entries(stats.commission_by_type || {}).map(([k, v]) => ({ name: TYPE_LABEL[k] || k, value: v })) : [];

  const StatCard = ({ icon, label, value, accent }) => (
    <div className="bg-white border border-stone-200 rounded-2xl p-5">
      <span className={`w-11 h-11 rounded-xl grid place-items-center ${accent || "bg-orange-50 text-orange-800"}`}>{icon}</span>
      <div className="text-stone-500 text-sm mt-3">{label}</div>
      <div className="font-display text-2xl font-bold text-stone-900">{value}</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex items-center gap-2">
          <ShieldCheck weight="fill" className="text-orange-800" size={22} />
          <span className="overline text-xs text-orange-800">Administration</span>
        </div>
        <h1 className="font-display text-4xl font-bold text-stone-900 mt-1">Console Admin</h1>

        {/* tabs */}
        <div className="flex flex-wrap gap-2 mt-6 border-b border-stone-200 pb-3">
          {TABS.map(([v, l, icon]) => (
            <button key={v} data-testid={`admin-tab-${v}`} onClick={() => setTab(v)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${tab === v ? "bg-slate-900 text-white" : "bg-white text-stone-600 border border-stone-200 hover:border-orange-300"}`}>
              {icon} {l}
            </button>
          ))}
        </div>

        {!stats ? (
          <div className="py-24 text-center text-stone-400">Chargement…</div>
        ) : tab === "overview" ? (
          <div className="mt-8" data-testid="admin-overview">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard icon={<Vault size={22} weight="duotone" />} label="Cautions encaissées" value={fmtMoney(stats.caution_total)} />
              <StatCard icon={<ChartPieSlice size={22} weight="duotone" />} label="Commissions totales (5%)" value={fmtMoney(stats.commission_total)} accent="bg-slate-900 text-orange-400" />
              <StatCard icon={<Buildings size={22} weight="duotone" />} label="Biens en ligne" value={stats.properties} />
              <StatCard icon={<Users size={22} weight="duotone" />} label="Utilisateurs" value={stats.users} />
            </div>
            <div className="grid lg:grid-cols-2 gap-4 mt-4">
              <div className="bg-white border border-stone-200 rounded-2xl p-6">
                <h2 className="font-display text-lg font-bold text-stone-900 mb-2">Commissions par type</h2>
                {pieData.length === 0 || pieData.every((d) => d.value === 0) ? (
                  <div className="py-16 text-center text-stone-400 text-sm">Aucune commission encaissée pour l'instant.</div>
                ) : (
                  <ResponsiveContainer width="100%" height={240}>
                    <PieChart>
                      <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={85} label>
                        {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={(v) => fmtMoney(v)} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>
              <div className="bg-slate-900 rounded-2xl p-6 text-white flex flex-col justify-center">
                <span className="overline text-xs text-orange-400">Revenu total encaissé</span>
                <div className="font-display text-4xl font-bold mt-2">{fmtMoney(stats.revenue_total)}</div>
                <div className="text-stone-300 text-sm mt-3">{stats.paid_bookings} paiement(s) confirmé(s) sur {stats.bookings} réservation(s).</div>
                <div className="text-stone-300 text-sm mt-1">Dont <b className="text-orange-400">{fmtMoney(stats.commission_total)}</b> de commissions plateforme.</div>
                <Button data-testid="export-csv-btn" onClick={exportCsv} className="mt-5 rounded-full bg-orange-800 hover:bg-orange-900 text-white w-fit">
                  <DownloadSimple size={18} className="mr-2" /> Exporter (CSV / Excel)
                </Button>
              </div>
            </div>
          </div>
        ) : tab === "cautions" ? (
          <div className="mt-8 bg-white border border-stone-200 rounded-2xl overflow-hidden" data-testid="admin-cautions">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 text-stone-500 text-left">
                <tr><th className="p-3">Bien</th><th className="p-3">Type</th><th className="p-3">Client</th><th className="p-3">Montant</th><th className="p-3">Commission</th><th className="p-3">Statut</th><th className="p-3">Fonds</th></tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {bookings.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-stone-400">Aucune réservation.</td></tr>}
                {bookings.map((b) => (
                  <tr key={b.id} data-testid={`admin-booking-${b.id}`}>
                    <td className="p-3 font-medium text-stone-900">{b.property_title}</td>
                    <td className="p-3"><span className="text-xs bg-orange-50 text-orange-800 px-2 py-0.5 rounded-full">{TYPE_LABEL[b.booking_type] || b.booking_type}</span></td>
                    <td className="p-3 text-stone-600">{b.renter_name}</td>
                    <td className="p-3 font-semibold">{fmtMoney(b.amount)}</td>
                    <td className="p-3 text-orange-800 font-semibold">{fmtMoney(b.commission || 0)}</td>
                    <td className="p-3">
                      <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${b.payment_status === "paid" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                        {b.payment_status === "paid" ? <><CheckCircle weight="fill" size={12} /> Payé</> : <><Clock size={12} /> En attente</>}
                      </span>
                    </td>
                    <td className="p-3">
                      {b.payment_status === "paid" && (
                        b.payout_status === "released"
                          ? <span className="text-xs text-green-700 font-semibold">Viré ✓</span>
                          : <Button data-testid={`payout-btn-${b.id}`} size="sm" variant="outline" onClick={() => releasePayout(b.id)} className="h-7 text-xs border-orange-300 text-orange-800">Virer au propriétaire</Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : tab === "properties" ? (
          <div className="mt-8 space-y-3" data-testid="admin-properties">
            {props.map((p) => (
              <div key={p.id} className="bg-white border border-stone-200 rounded-2xl p-4 flex items-center gap-4">
                <img src={p.media?.[0] || "https://images.unsplash.com/photo-1564078516393-cf04bd966897?w=200&q=70"} alt="" className="w-24 h-20 object-cover rounded-lg" />
                <div className="flex-1">
                  <h3 className="font-display font-bold text-stone-900">{p.title}</h3>
                  <div className="text-xs text-stone-500">{p.city}{p.country ? `, ${p.country}` : ""} · par {p.owner_name}</div>
                  <span className={`inline-block mt-1 text-[11px] px-2 py-0.5 rounded-full font-semibold ${p.verification_status === "certified" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                    {p.verification_status === "certified" ? "Certifié" : "À vérifier"}
                  </span>
                </div>
                {p.ownership_document && (
                  <a href={p.ownership_document} target="_blank" rel="noreferrer" className="text-xs text-orange-800 underline shrink-0">Document</a>
                )}
                {p.verification_status === "certified"
                  ? <Button data-testid={`uncertify-btn-${p.id}`} size="sm" variant="outline" onClick={() => certifyProp(p.id, false)} className="h-8 text-xs">Retirer</Button>
                  : <Button data-testid={`certify-btn-${p.id}`} size="sm" onClick={() => certifyProp(p.id, true)} className="h-8 text-xs bg-green-700 hover:bg-green-800 text-white">Certifier</Button>}
                <Button data-testid={`admin-del-prop-${p.id}`} size="sm" variant="ghost" onClick={() => delProp(p.id)} className="text-stone-400 hover:text-red-600"><Trash size={16} /></Button>
              </div>
            ))}
          </div>
        ) : tab === "users" ? (
          <div className="mt-8 bg-white border border-stone-200 rounded-2xl overflow-hidden" data-testid="admin-users">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 text-stone-500 text-left"><tr><th className="p-3">Nom</th><th className="p-3">Email</th><th className="p-3">Rôle</th><th className="p-3">Statut</th><th className="p-3 text-right">Actions</th></tr></thead>
              <tbody className="divide-y divide-stone-100">
                {users.map((u) => (
                  <tr key={u.user_id} data-testid={`admin-user-${u.user_id}`} className={u.blocked ? "bg-red-50/40" : ""}>
                    <td className="p-3 font-medium text-stone-900">{u.name}</td>
                    <td className="p-3 text-stone-600">{u.email}</td>
                    <td className="p-3 w-40">
                      <Select value={u.role} onValueChange={(v) => setUserRole(u.user_id, v)}>
                        <SelectTrigger data-testid={`role-select-${u.user_id}`} className="h-8"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="buyer">Acheteur</SelectItem>
                          <SelectItem value="seller">Vendeur</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="p-3">
                      {u.blocked
                        ? <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-semibold">Bloqué</span>
                        : <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-semibold">Actif</span>}
                    </td>
                    <td className="p-3 text-right">
                      <Button data-testid={`block-btn-${u.user_id}`} size="sm" variant="ghost" onClick={() => toggleBlock(u)}
                        className={u.blocked ? "text-green-700 hover:text-green-800" : "text-stone-400 hover:text-red-600"}>
                        {u.blocked ? <><ArrowCounterClockwise size={15} className="mr-1" /> Débloquer</> : <><Prohibit size={15} className="mr-1" /> Bloquer</>}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="mt-8 max-w-xl bg-white border border-stone-200 rounded-2xl p-6 space-y-4" data-testid="admin-settings">
            <h2 className="font-display text-lg font-bold text-stone-900">Modifier le site</h2>
            <div><Label>Nom du site</Label><Input data-testid="settings-site-name" value={form.site_name} onChange={(e) => setForm({ ...form, site_name: e.target.value })} className="mt-1" /></div>
            <div><Label>Titre d'accueil (hero)</Label><Input data-testid="settings-hero-title" value={form.hero_title} onChange={(e) => setForm({ ...form, hero_title: e.target.value })} className="mt-1" /></div>
            <div><Label>Sous-titre d'accueil</Label><Textarea data-testid="settings-hero-subtitle" value={form.hero_subtitle} onChange={(e) => setForm({ ...form, hero_subtitle: e.target.value })} rows={3} className="mt-1" /></div>
            <div><Label>Taux de commission (ex: 0.10 = 10%)</Label><Input data-testid="settings-commission" type="number" step="0.01" min="0" max="1" value={form.commission_rate} onChange={(e) => setForm({ ...form, commission_rate: e.target.value })} className="mt-1" /></div>
            <Button data-testid="settings-save-btn" onClick={saveSettings} className="rounded-full bg-orange-800 hover:bg-orange-900 text-white">Enregistrer</Button>
          </div>
        )}
      </div>
    </div>
  );
}
